// STATUSPAGE1 (docs/product/enterprise-basics-recommendations.md section 6; owner ruling DEC-514): the "System status"
// row in the account menu of the location console (managers), the franchisor console and Snip & Sip Admin. It opens the
// hidden public status page in a new tab. The address comes from the server's SUPPORT_STATUS_URL setting, read once
// from /health; with no address the row stays hidden. Nothing here is a banner, and the floor screens never get it.

/** Accepts only an https address; anything else is treated as "no link". Pure. */
export function validStatusUrl(value: unknown): string | undefined {
  if (typeof value !== "string" || value.trim() === "") return undefined;
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** Reads the address from /health. Any failure (offline, server down, no setting) gives undefined: no link. */
export async function fetchStatusUrl(fetchImpl: typeof fetch = fetch): Promise<string | undefined> {
  try {
    const response = await fetchImpl("/health", { cache: "no-store", credentials: "omit" });
    if (!response.ok) return undefined;
    const body: unknown = await response.json();
    return typeof body === "object" && body !== null
      ? validStatusUrl((body as { supportStatusUrl?: unknown }).supportStatusUrl)
      : undefined;
  } catch {
    return undefined;
  }
}

export interface StatusLink {
  /** Whether this person may see the row at all (the location console passes the manager gate). */
  setAllowed(allowed: boolean): void;
}

/** Wires the row (an <a>). It shows only when an address is set AND the person is allowed. */
export function wireStatusLink(anchor: HTMLAnchorElement, fetchImpl: typeof fetch = fetch): StatusLink {
  let url: string | undefined;
  let allowed = true;
  const apply = (): void => {
    if (url === undefined || !allowed) {
      anchor.hidden = true;
      anchor.removeAttribute("href");
      return;
    }
    anchor.href = url;
    anchor.hidden = false;
  };
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  apply();
  void fetchStatusUrl(fetchImpl).then((found) => {
    url = found;
    apply();
  });
  return {
    setAllowed(next: boolean): void {
      allowed = next;
      apply();
    },
  };
}
